import { combineLatest, type Observable } from 'rxjs';
import {
  type APIFieldAttributes,
  type APIWorkspaceAttributes,
  type ArdoqId,
  type ComponentTypeHierarchy,
  PersonalSetting,
} from '@ardoq/api-types';
import { action$, reducer, reduceState, streamReducer } from '@ardoq/rxbeach';
import { context$ } from 'streams/context/context$';
import type {
  ContextShape,
  ScenarioModeState,
  WorkspaceAndModelLookup,
} from '@ardoq/data-model';
import {
  Permissions,
  isDisabled,
} from 'streams/currentUserPermissions/permissionInterface';
import {
  getConnectedWorkspaceIdsDeep,
  linkedWorkspaces$,
  type LinkedWorkspaces,
} from 'streams/linkedWorkspaces$';
import {
  CalculatedFieldEventStatus,
  type CalculatedFieldEvent,
  type CalculatedFieldEventMap,
} from './types';
import {
  setCalculatedFieldEvent,
  setUserCalculatedFieldEventsAsNotified,
} from './actions';
import { getCalculatedFieldDoneEventsFromUser } from './utils';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { type PermissionContext } from '@ardoq/access-control';
import type { APICurrentUser } from '@ardoq/api-types';
import { dishonestCurrentUser$ as currentUser$ } from 'streams/currentUser/currentUser$';
import { activeScenario$ } from '../../../streams/activeScenario/activeScenario$';
import { scenarioInterface } from 'modelInterface/scenarios/scenarioInterface';
import {
  type FeatureContext,
  featureOperations,
  Features,
} from '@ardoq/features';
import { activeScenarioOperations, fieldOperations } from '@ardoq/core';
import { fields$ } from 'streams/fields/fields$';
import { featureContext$ } from 'streams/featureContext$';
import { isTruthy } from '@ardoq/common-helpers';
import { workspaceAndModel$ } from 'streams/workspaceAndModel$';
import { scopeDataOperations } from '@ardoq/scope-data';
import { StreamCollection } from 'streams/utils/streamUtils';
import { type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { getDefaultLocale, type Locale } from '@ardoq/locale';
import { locale$ } from 'streams/locale$';

export interface WorkspaceOptionsState {
  hideModelPreview: boolean;
  showCreateTemplate: boolean;
  showEditModel: boolean;
  contextWorkspace: APIWorkspaceAttributes | undefined;
  isFlexibleModel: boolean;
  isWsWriter: boolean;
  isWsAdmin: boolean;
  disableEditWorkspace: boolean;
  disableExportExcel: boolean;
  disableEditModel: boolean;
  disableWorkspaceEditReferenceTypes: boolean;
  disableWorkspaceEditFields: boolean;
  disableModelCreateTemplate: boolean;
  hasRelatedWorkspaces: boolean;
  relatedWorkspaces: { id: string; name: string }[];
  calculatedFields: APIFieldAttributes[];
  calculatedFieldEvents: CalculatedFieldEventMap;
  componentTypeHierarchy: ComponentTypeHierarchy;
  branchId?: ArdoqId | null;
  showCalculatedFieldSection: boolean;
  locale: Locale;
  i18nLocale: I18nLocale;
}

const initialState: WorkspaceOptionsState = {
  contextWorkspace: undefined,
  hideModelPreview: false,
  isFlexibleModel: false,
  showCreateTemplate: true,
  showEditModel: false,
  isWsWriter: false,
  isWsAdmin: false,
  disableEditWorkspace: true,
  disableExportExcel: true,
  disableEditModel: true,
  disableWorkspaceEditReferenceTypes: true,
  disableWorkspaceEditFields: true,
  disableModelCreateTemplate: true,
  hasRelatedWorkspaces: false,
  relatedWorkspaces: [],
  calculatedFields: [],
  calculatedFieldEvents: {},
  componentTypeHierarchy: {},
  showCalculatedFieldSection: false,
  locale: getDefaultLocale(),
  i18nLocale: {},
};

const getCalculatedFields = ({
  workspace,
  fields,
}: {
  workspace: APIWorkspaceAttributes;
  fields: StreamCollection<APIFieldAttributes>;
}): APIFieldAttributes[] => {
  const isCalculatedAndInModel = (field: APIFieldAttributes) =>
    fieldOperations.isCalculatedField(field) &&
    field.model === workspace.componentModel;

  return fields.models.filter(isCalculatedAndInModel);
};

const contextReducer = (
  state: WorkspaceOptionsState,
  {
    context,
    permissionContext,
    activeScenarioState,
    linkedWorkspaces,
    featureContext,
    workspaceAndModel,
    fields,
    currentUser,
    locale,
    i18nLocale,
  }: {
    context: ContextShape;
    permissionContext: PermissionContext;
    activeScenarioState: ScenarioModeState;
    linkedWorkspaces: LinkedWorkspaces;
    featureContext: FeatureContext;
    workspaceAndModel: WorkspaceAndModelLookup;
    fields: StreamCollection<APIFieldAttributes>;
    currentUser: APICurrentUser | null;
    locale: Locale;
    i18nLocale: I18nLocale;
  }
): WorkspaceOptionsState => {
  const contextWorkspaceId = context.workspaceId;
  const result = scopeDataOperations.getWorkspaceAndModel(
    workspaceAndModel,
    contextWorkspaceId
  );

  if (!result) {
    return { ...initialState, locale, i18nLocale };
  }

  const { workspace, model } = result;

  const relatedWorkspaces = getConnectedWorkspaceIdsDeep(
    contextWorkspaceId,
    linkedWorkspaces
  )
    // In presentation mode we can still get workspace ids we don't have access
    // to.
    .map(id => scopeDataOperations.getWorkspace(workspaceAndModel, id))
    .filter(isTruthy)
    .map(({ _id, name }) => ({
      id: _id,
      name: name,
    }));

  const disableModelCreateTemplate = isDisabled(
    Permissions.MODEL_CREATE_TEMPLATE,
    workspace.componentModel
  );

  const hideIfDisabledRelatedWorkspaces = isDisabled(
    Permissions.WORKSPACE_RELATED_WORKSPACES
  );
  const hasQuickStartFeature = featureOperations.hasFeature(
    featureContext,
    Features.QUICK_START
  );
  const hasDisableCreateTemplateFeature = featureOperations.hasFeature(
    featureContext,
    Features.DISABLE_CREATE_TEMPLATE_FROM_WORKSPACE_METAMODEL
  );
  const hasBranchedGraphFeature = featureOperations.hasFeature(
    featureContext,
    Features.BRANCHED_GREMLIN_GRAPH
  );

  const isWsAdmin = workspaceAccessControlInterface.canAdminWorkspace(
    permissionContext,
    contextWorkspaceId,
    activeScenarioState
  );

  const isWsWriter = workspaceAccessControlInterface.canEditWorkspace(
    permissionContext,
    contextWorkspaceId,
    activeScenarioState
  );

  const branchId = scenarioInterface.getScopeAndBranchId(
    activeScenarioOperations.getActiveScenarioId(activeScenarioState)
  ).branchId;

  const calculatedFields = getCalculatedFields({
    workspace,
    fields,
  });

  const showCalculatedFieldSection =
    calculatedFields.length > 0 &&
    isWsWriter &&
    (branchId ? hasBranchedGraphFeature : true);

  return {
    contextWorkspace: workspace,
    hasRelatedWorkspaces:
      !hideIfDisabledRelatedWorkspaces &&
      relatedWorkspaces &&
      !!relatedWorkspaces.length,
    relatedWorkspaces: relatedWorkspaces,
    isFlexibleModel: Boolean(model.flexible),
    isWsWriter,
    isWsAdmin,
    showEditModel: isWsAdmin && !hasQuickStartFeature,
    showCreateTemplate: !hasDisableCreateTemplateFeature,
    hideModelPreview: Boolean(
      currentUser?.settings[PersonalSetting.HIDE_MODEL_PREVIEW]
    ),
    calculatedFields: calculatedFields,
    calculatedFieldEvents: state.calculatedFieldEvents,
    disableEditWorkspace: isDisabled(Permissions.WORKSPACE_EDIT),
    disableEditModel: isDisabled(Permissions.MODEL_EDIT),
    disableWorkspaceEditFields: isDisabled(Permissions.WORKSPACE_EDIT_FIELDS),
    disableWorkspaceEditReferenceTypes: isDisabled(
      Permissions.WORKSPACE_EDIT_REFERENCE_TYPES
    ),
    disableModelCreateTemplate,
    disableExportExcel: isDisabled(Permissions.WORKSPACE_EXPORT_EXCEL),
    componentTypeHierarchy: model.root,
    branchId,
    showCalculatedFieldSection,
    locale,
    i18nLocale,
  };
};

const handleSetCalculatedFieldEvent = (
  state: WorkspaceOptionsState,
  calculatedFieldEvent: CalculatedFieldEvent
) => ({
  ...state,
  calculatedFieldEvents: {
    ...state.calculatedFieldEvents,
    [calculatedFieldEvent.fieldId]: calculatedFieldEvent,
  },
});

const handleSetUserCalculatedFieldEventsAsNotified = (
  state: WorkspaceOptionsState,
  userId: string
): WorkspaceOptionsState => ({
  ...state,
  calculatedFieldEvents: {
    ...state.calculatedFieldEvents,
    ...Object.fromEntries(
      Object.entries(
        getCalculatedFieldDoneEventsFromUser(
          state.calculatedFieldEvents,
          userId
        )
      ).map(([fieldId, event]) => [
        fieldId,
        { ...event, status: CalculatedFieldEventStatus.DONE_NOTIFIED },
      ])
    ),
  },
});

const workspaceOptionContext$ = combineLatest({
  context: context$,
  permissionContext: currentUserPermissionContext$,
  workspaceAndModel: workspaceAndModel$,
  activeScenarioState: activeScenario$,
  linkedWorkspaces: linkedWorkspaces$,
  featureContext: featureContext$,
  fields: fields$,
  currentUser: currentUser$,
  locale: locale$,
  i18nLocale: i18nLocale$,
});

export const workspaceOptions$: Observable<WorkspaceOptionsState> =
  action$.pipe(
    reduceState('workspaceOptions$', initialState, [
      streamReducer(workspaceOptionContext$, contextReducer),
      reducer(setCalculatedFieldEvent, handleSetCalculatedFieldEvent),
      reducer(
        setUserCalculatedFieldEventsAsNotified,
        handleSetUserCalculatedFieldEventsAsNotified
      ),
    ])
  );
